import React, { useState, useEffect } from 'react';
import { Modal, Box } from '@mui/material';
import Select from 'react-select';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { 
    FaUserTie, 
    FaUser, 
    FaEnvelope, 
    FaPhone, 
    FaBuilding, 
    FaDollarSign,
    FaInfoCircle,
    FaSave,
    FaSpinner,
    FaMoneyBillWave,
    FaMapMarkerAlt,
    FaGraduationCap,
    FaFileContract,
    FaCalendarAlt,
    FaBriefcase,
    FaFile,
    FaDownload,
    FaUpload,
    FaChild,
    FaPlus,
    FaTrash
} from 'react-icons/fa';
import './UpdateHrManager.css';

const UpdateHrManager = ({ 
    open, 
    onClose, 
    formData, 
    handleChange, 
    handleSubmit, 
    errors, 
    loading,
    departments,
    selectedDepartment,
    handleDepartmentChange,
    handleDateChange
}) => {
    const [children, setChildren] = useState(formData.children || []);

    // Add/remove modal-open class to body
    useEffect(() => {
        if (open) {
            document.body.classList.add('modal-open');
        } else {
            document.body.classList.remove('modal-open');
        }
        
        // Cleanup on unmount
        return () => {
            document.body.classList.remove('modal-open');
        };
    }, [open]);

    if (!open) return null;

    // Helper functions for date formatting
    const formatDateForInput = (dateString) => {
        if (!dateString) return null;
        return dayjs(dateString);
    };

    // If handleDateChange wasn't passed as a prop, create a local version
    const onDateChange = handleDateChange || ((date, fieldName) => {
        if (date) {
            const formattedDate = date.format('YYYY-MM-DD');
            handleChange({
                target: {
                    name: fieldName,
                    value: formattedDate
                }
            });
        } else {
            handleChange({
                target: {
                    name: fieldName,
                    value: ''
                }
            });
        }
    });

    // Handle document file change
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const event = {
                target: {
                    name: 'document_file',
                    value: file
                }
            };
            handleChange(event);
        }
    };

    // Handle document download
    const handleDocumentDownload = () => {
        if (formData.documents && formData.documents.document_file) {
            window.open(formData.documents.document_file, '_blank');
        }
    };

    // Handle children management
    const handleAddChild = () => {
        setChildren([...children, { name: '', age: '', gender: '' }]);
    };

    const handleRemoveChild = (index) => {
        const newChildren = children.filter((_, i) => i !== index);
        setChildren(newChildren);
        handleChange({
            target: {
                name: 'children',
                value: newChildren
            }
        });
    };

    const handleChildChange = (index, field, value) => {
        const newChildren = [...children];
        newChildren[index] = {
            ...newChildren[index],
            [field]: value
        };
        setChildren(newChildren);
        handleChange({
            target: {
                name: 'children',
                value: newChildren
            }
        });
    };

    return (
        <div className="update-modal">
            <div className="modal-content">
                <div className="modal-header">
                    <div className="header-content">
                        <FaUserTie className="header-icon" />
                        <div>
                            <h2>Update Employee Profile</h2>
                            <p className="header-subtitle">Update employee information and settings</p>
                        </div>
                    </div>
                    <button className="close-modal-btn" onClick={onClose}>&times;</button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="modal-body">
                        {/* Personal Information Section */}
                        <div className="form-section">
                            <div className="section-header">
                                <FaUser className="section-icon" />
                                <h3>Personal Information</h3>
                            </div>
                            <div className="form-grid">
                                <div className="form-group">
                                    <label>
                                        <FaEnvelope className="field-icon" />
                                        Email <span className="required">*</span>
                                    </label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        className={errors.email ? 'error' : ''}
                                    />
                                    {errors.email && <div className="error-message">{errors.email}</div>}
                                </div>

                                <div className="form-group">
                                    <label>
                                        <FaUser className="field-icon" />
                                        First Name <span className="required">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="first_name"
                                        value={formData.first_name}
                                        onChange={handleChange}
                                        className={errors.first_name ? 'error' : ''}
                                    />
                                    {errors.first_name && <div className="error-message">{errors.first_name}</div>}
                                </div>

                                <div className="form-group">
                                    <label>
                                        <FaUser className="field-icon" />
                                        Last Name <span className="required">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="last_name"
                                        value={formData.last_name}
                                        onChange={handleChange}
                                        className={errors.last_name ? 'error' : ''}
                                    />
                                    {errors.last_name && <div className="error-message">{errors.last_name}</div>}
                                </div>

                                <div className="form-group">
                                    <label>
                                        <FaPhone className="field-icon" />
                                        Phone Number <span className="required">*</span>
                                    </label>
                                    <input
                                        type="tel"
                                        name="phone_number"
                                        value={formData.phone_number}
                                        onChange={handleChange}
                                        className={errors.phone_number ? 'error' : ''}
                                    />
                                    {errors.phone_number && <div className="error-message">{errors.phone_number}</div>}
                                </div>
                            </div>
                        </div>

                        {/* Work Information Section */}
                        <div className="form-section">
                            <div className="section-header">
                                <FaBriefcase className="section-icon" />
                                <h3>Work Information</h3>
                            </div>
                            <div className="form-grid">
                                <div className="form-group">
                                    <label>
                                        <FaBuilding className="field-icon" />
                                        Department <span className="required">*</span>
                                    </label>
                                    <Select
                                        value={selectedDepartment}
                                        onChange={handleDepartmentChange}
                                        options={departments}
                                        className={errors.department ? 'react-select-container error' : 'react-select-container'}
                                        classNamePrefix="react-select"
                                    />
                                    {errors.department && <div className="error-message">{errors.department}</div>}
                                </div>

                                <div className="form-group">
                                    <label>
                                        <FaCalendarAlt className="field-icon" />
                                        Working Days per Month
                                    </label>
                                    <input
                                        type="number"
                                        name="working_days_per_month"
                                        value={formData.working_days_per_month || '22'}
                                        onChange={handleChange}
                                        min="0"
                                        max="31"
                                        className="number-input"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>
                                        <FaMoneyBillWave className="field-icon" />
                                        Salary <span className="required">*</span>
                                    </label>
                                    <input
                                        type="number"
                                        name="salary"
                                        value={formData.salary}
                                        onChange={handleChange}
                                        className={errors.salary ? 'error' : ''}
                                    />
                                    {errors.salary && <div className="error-message">{errors.salary}</div>}
                                </div>

                                <div className="form-group">
                                    <label>
                                        <FaFileContract className="field-icon" />
                                        Contract Type <span className="required">*</span>
                                    </label>
                                    <select
                                        name="contract_type"
                                        value={formData.contract_type}
                                        onChange={handleChange}
                                        className={errors.contract_type ? 'error' : ''}
                                    >
                                        <option value="">Select Contract Type</option>
                                        <option value="permanent">Permanent</option>
                                        <option value="contract">Contract</option>
                                        <option value="temporary">Temporary</option>
                                    </select>
                                    {errors.contract_type && <div className="error-message">{errors.contract_type}</div>}
                                </div>
                            </div>
                        </div>

                        {/* Photo Upload Section */}
                        <div className="form-section">
                            <div className="section-header">
                                <FaUser className="section-icon" />
                                <h3>Profile Photo</h3>
                            </div>
                            <div className="photo-section">
                                <div className="photo-upload-container">
                                    {formData.photo_url ? (
                                        <div className="photo-preview">
                                            <img src={formData.photo_url} alt="Employee" />
                                            <div className="photo-overlay">
                                                <FaUpload className="upload-icon" />
                                                <span>Change Photo</span>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="photo-placeholder">
                                            <FaUser className="placeholder-icon" />
                                            <span>Upload Photo</span>
                                        </div>
                                    )}
                                    <input
                                        type="file"
                                        name="photo"
                                        onChange={handleFileChange}
                                        accept="image/*"
                                        className="photo-input"
                                        id="photo-upload"
                                    />
                                </div>
                                <p className="photo-help">Upload a professional passport-sized photo (Max 5MB)</p>
                            </div>
                        </div>

                        {/* Children Information Section */}
                        <div className="form-section">
                            <div className="section-header">
                                <FaChild className="section-icon" />
                                <h3>Children Information</h3>
                            </div>
                            <div className="children-container">
                                {children.map((child, index) => (
                                    <div key={index} className="child-form">
                                        <div className="child-header">
                                            <h4>Child {index + 1}</h4>
                                            <button
                                                type="button"
                                                className="remove-child-btn"
                                                onClick={() => handleRemoveChild(index)}
                                            >
                                                <FaTrash />
                                            </button>
                                        </div>
                                        <div className="child-form-row">
                                            <div className="form-group">
                                                <label>Name <span className="required">*</span></label>
                                                <input
                                                    type="text"
                                                    value={child.name}
                                                    onChange={(e) => handleChildChange(index, 'name', e.target.value)}
                                                    placeholder="Child's name"
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label>Age <span className="required">*</span></label>
                                                <input
                                                    type="number"
                                                    value={child.age}
                                                    onChange={(e) => handleChildChange(index, 'age', e.target.value)}
                                                    placeholder="Age"
                                                    min="0"
                                                    max="18"
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label>Gender <span className="required">*</span></label>
                                                <select
                                                    value={child.gender}
                                                    onChange={(e) => handleChildChange(index, 'gender', e.target.value)}
                                                >
                                                    <option value="">Select Gender</option>
                                                    <option value="male">Male</option>
                                                    <option value="female">Female</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    className="add-child-btn"
                                    onClick={handleAddChild}
                                >
                                    <FaPlus /> Add Child
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="modal-footer">
                        <button type="button" className="cancel-btn" onClick={onClose}>
                            Cancel
                        </button>
                        <button type="submit" className="submit-btn" disabled={loading}>
                            {loading ? (
                                <>
                                    <FaSpinner className="spinner-icon" />
                                    <span>Updating...</span>
                                </>
                            ) : (
                                <>
                                    <FaSave />
                                    <span>Save Changes</span>
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default UpdateHrManager; 